import { BaseRepository } from './BaseRepository';
import { supabase } from '../supabase';

export interface SWCustomer {
  id: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  company_id?: string;
  created_at: string;
}

export interface SWSyncState {
  company_id: string;
  last_sync_status: string;
  last_incremental_sync: string;
  total_customers_synced: number;
}

export interface SWActivityStats {
  totalCustomers: number;
  totalServiceAccounts: number;
  totalLocations: number;
  recentImports: number;
}

export class SuccessWareRepository extends BaseRepository<SWCustomer> {
  constructor() {
    super({ tableName: 'sw_customers', primaryKey: 'id' });
  }

  /**
   * Get activity statistics
   */
  async getActivityStats(): Promise<SWActivityStats> {
    const [customers, serviceAccounts, locations] = await Promise.all([
      supabase.from('sw_customers').select('id', { count: 'exact', head: true }),
      supabase.from('sw_service_accounts').select('id', { count: 'exact', head: true }),
      supabase.from('sw_locations').select('id', { count: 'exact', head: true })
    ]);

    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    const { count: recentCount } = await supabase
      .from('sw_customers')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', fiveMinutesAgo);

    return {
      totalCustomers: customers.count || 0,
      totalServiceAccounts: serviceAccounts.count || 0,
      totalLocations: locations.count || 0,
      recentImports: recentCount || 0
    };
  }

  /**
   * Get recent customer activity
   */
  async getRecentActivity(limit: number = 50): Promise<SWCustomer[]> {
    const { data, error } = await supabase
      .from(this.getTableName())
      .select('id, company_name, first_name, last_name, company_id, created_at')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  /**
   * Get sync states
   */
  async getSyncStates(): Promise<SWSyncState[]> {
    const { data, error } = await supabase
      .from('sw_sync_state')
      .select('company_id, last_sync_status, last_incremental_sync, total_customers_synced')
      .order('last_incremental_sync', { ascending: false });

    if (error) throw error;
    return data || [];
  }
}
